import React, { useEffect, useState } from 'react';
import Toggle from './Toggle';

/**
 * Sidebar card: links that scroll to each card, and the "Show empty fields" switch.
 *
 * `sections` is [{ id, label, icon, badge }], only for cards that are on the page. The active link
 * follows scrolling (the last card whose top has passed the site's fixed header).
 *
 * The links are hidden below `lg`: the sidebar stacks under the cards there, so a nav at the bottom
 * of the page would only scroll back up. The switch stays.
 */
const SectionNav = ({ sections, showEmpty, onToggleShowEmpty }) => {
    const [activeId, setActiveId] = useState(sections[0]?.id);
    const ids = sections.map(s => s.id).join(',');

    useEffect(() => {
        const HEADER_OFFSET = 170;
        const onScroll = () => {
            let current = sections[0]?.id;
            sections.forEach(({ id }) => {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top - HEADER_OFFSET <= 0) current = id;
            });
            setActiveId(current);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
        // `ids` stands in for `sections`, which is a new array on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids]);

    const goTo = id => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveId(id);
    };

    return (
        <nav aria-label="Booking sections" className="booking-detail-page tw-mb-6 tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white">
            <ul className="tw-py-1 max-lg:tw-hidden">
                {sections.map(({ id, label, icon: Icon, badge }) => {
                    const active = id === activeId;
                    return (
                        <li key={id}>
                            <button
                                type="button"
                                onClick={() => goTo(id)}
                                aria-current={active ? 'location' : undefined}
                                className={`tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-border-l-2 tw-px-4 tw-py-2 tw-text-left tw-text-sm ${active ? 'tw-border-brand-600 tw-bg-brand-50 tw-font-medium tw-text-brand-700' : 'tw-border-transparent tw-text-neutral-700 hover:tw-bg-neutral-50'}`}
                            >
                                {Icon ? <Icon className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden="true" /> : null}
                                <span className="tw-flex-1">{label}</span>
                                {badge
                                    ? <span className="tw-inline-flex tw-h-5 tw-min-w-[20px] tw-items-center tw-justify-center tw-rounded-full tw-bg-red-600 tw-px-1.5 tw-text-[11px] tw-font-semibold tw-text-white">{badge}</span>
                                    : null}
                            </button>
                        </li>
                    );
                })}
            </ul>
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-4 tw-py-2.5 tw-text-sm tw-text-neutral-600 lg:tw-border-t lg:tw-border-neutral-200">
                <Toggle checked={showEmpty} onChange={onToggleShowEmpty} label="Show empty fields" />
                Show empty fields
            </div>
        </nav>
    );
};

export default SectionNav;
